import { CSSProperties, PropsWithChildren, ReactElement } from 'react';
import { KeysOfType } from '@synergy/utils';
import { Palette, PaletteColor, PaletteColorOptions, ThemeProvider, createTheme } from '@mui/material/styles';

import * as MuiAccordion from './components/accordion';
import * as MuiAlert from './components/alert';
import * as MuiAvatar from './components/avatar';
import * as MuiBadge from './components/badge';
import * as MuiDialog from './components/dialog';
import * as MuiInput from './components/input';
import * as MuiList from './components/list';
import * as MuiMenu from './components/menu';
import * as MuiStepper from './components/stepper';
import * as MuiTable from './components/table';
import { MuiAutocomplete } from './components/autocomplete';
import { MuiButton } from './components/button';
import { MuiChip } from './components/chip';
import { MuiCssBaseline } from './global/css-baseline';
import { MuiDatePicker } from './components/date-picker';
import { MuiFormControlLabel } from './components/form-control-label';
import { MuiFormHelperText } from './components/form-helper-text';
import { MuiFormLabel } from './components/form-label';
import { MuiIconButton } from './components/icon-button';
import { MuiPagination, MuiPaginationItem } from './components/pagination';
import { MuiSelect } from './components/select';
import { MuiSvgIcon } from './components/svg-icon';
import { MuiTab, MuiTabs } from './components/tab';
import { MuiToggleButton } from './components/toggle-button';
import { MuiTooltip } from './components/tooltip';
import { MuiTypography } from './components/typography';
import { breakpoints } from './global/breakpoints';
import { palette } from './global/palette';
import { typography } from './global/typography';

declare module '@mui/material' {
    interface Color {
        850: string;
    }
}

declare module '@mui/material/Alert' {
    interface AlertPropsColorOverrides {
        null: true;
    }
}

declare module '@mui/material/styles' {
    interface Palette {
        successDark: Palette['primary'];
        dark: Palette['primary'];
        medium: Palette['primary'];
        light: Palette['primary'];
        negative: Palette['primary'];
        tableBorder: Palette['primary'];
        successNotificationBanner: Palette['primary'];
        alertInfo: Palette['primary'];
        alertSuccess: Palette['primary'];
        alertWarning: Palette['primary'];
        alertError: Palette['primary'];
    }

    interface PaletteOptions {
        successDark?: PaletteOptions['primary'];
        topBar?: PaletteColorOptions;
        dark?: PaletteOptions['primary'];
        medium?: PaletteOptions['primary'];
        light?: PaletteOptions['primary'];
        negative?: PaletteOptions['primary'];
        tableBorder?: PaletteOptions['primary'];
        successNotificationBanner?: PaletteOptions['primary'];
        alertInfo?: PaletteOptions['primary'];
        alertSuccess?: PaletteOptions['primary'];
        alertWarning?: PaletteOptions['primary'];
        alertError?: PaletteOptions['primary'];
    }

    interface TypographyVariants {
        statusLabel: CSSProperties;
        h2Uppercase: CSSProperties;
    }

    type TypographyOptions = Partial<TypographyVariants>;
}

declare module '@mui/material/Chip' {
    interface ChipPropsColorOverrides {
        negative: true;
        dark: true;
        light: true;
        successDark: true;
    }
}

declare module '@mui/material/Checkbox' {
    interface CheckboxPropsColorOverrides {
        dark: true;
    }
}

declare module '@mui/material/TableCell' {
    interface TableCellProps {
        hide?: keyof typeof breakpoints.values;
    }
}

export type ColorOptions = 'inherit' | KeysOfType<Palette, PaletteColor>;
export type ChipColorOptions = 'default' | KeysOfType<Palette, PaletteColor>;

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        link: true;
    }
}

export const theme = createTheme({
    components: {
        MuiCssBaseline,
        ...MuiInput,
        ...MuiMenu,
        MuiButton,
        MuiIconButton,
        MuiSelect,
        MuiAutocomplete,
        MuiSvgIcon,
        ...MuiDialog,
        MuiFormHelperText,
        ...MuiList,
        ...MuiBadge,
        ...MuiAvatar,
        ...MuiAccordion,
        ...MuiTable,
        MuiTypography,
        MuiPagination,
        MuiPaginationItem,
        MuiChip,
        MuiTooltip,
        MuiDatePicker,
        MuiTab,
        MuiTabs,
        MuiFormControlLabel,
        MuiFormLabel,
        ...MuiStepper,
        ...MuiAlert,
        MuiToggleButton,
    },
    palette,
    breakpoints,
    typography,
});

export const ThemeWrapper = (props: PropsWithChildren): ReactElement => (
    <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
);
